import React, { useEffect, useMemo } from "react";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { fetchVehicleMaintenanceById, markVehicleMaintenanceDone } from "../../framework/redux/slices/vehicleMaintenanceSlice";
import { addSuccessMsg } from "../../framework/redux/slices/appSlice";
import { handleApiError } from "../../services/baseService";
import { Base } from "../../framework/common/base";
import type { VehicleMaintenanceDto } from "../../models/vehicleMaintenance/vehicleMaintenance";
import { MaintenanceReminderStatus } from "../../models/vehicleMaintenance/vehicleMaintenanceReminder";
import { isServiceEditableForDriver } from "./vehicleMaintenanceUtils";

type VehicleMaintenanceSliceShape = {
    items: VehicleMaintenanceDto[];
};

export const VehicleMaintenanceDetails = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();

    const item: VehicleMaintenanceDto | undefined = useAppSelector(
        (s) => (s.vehicleMaintenance as VehicleMaintenanceSliceShape).items.find((m) => m.id === id)
    );

    useEffect(() => {
        if (id && !item) {
            dispatch(fetchVehicleMaintenanceById(id)).unwrap().catch(handleApiError);
        }
    }, [id, !!item]);

    const canEdit = useMemo(() => {
        if (!item) return false;
        return isServiceEditableForDriver(item);
    }, [item]);

    const canShowMarkDone = useMemo(() => {
        if (!item) return false;
        if (item.reminder?.status === MaintenanceReminderStatus.Completed) return false;
        return true;
    }, [item]);

    const canMarkDone = useMemo(() => {
        if (!item) return false;

        if (!canShowMarkDone) return false;

        if (item.reminder?.status == null) return true;

        return item.reminder.status !== MaintenanceReminderStatus.Completed;
    }, [canShowMarkDone, item]);

    const markDoneDisabledReason = useMemo(() => {
        if (!item) return null;

        if (item.reminder?.status == null) return null;
        if (item.reminder.status === MaintenanceReminderStatus.Completed) return "completed";
        if (item.reminder.status === MaintenanceReminderStatus.AwaitingApproval) return "awaiting";
        return null;
    }, [item?.reminder?.status]);

    if (!id) {
        return null;
    }

    return (
        <MainLayout topComponent={<PageTitle header={t("maintenance.detailsTitle")} />} hasPx>
            {!item ? (
                <Typography>{t("common.loading")}</Typography>
            ) : (
                <Box>
                    {item.title ? (
                        <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                            {item.title}
                        </Typography>
                    ) : null}

                    {item.reminder?.status != null ? (
                        <Typography sx={{ mb: 1 }}>
                            {t("maintenance.reminderStatus")}: {t(`maintenance.reminderStatusValues.${item.reminder.status}`, { defaultValue: item.reminder.status })}
                        </Typography>
                    ) : null}

                    <Typography sx={{ mb: 1 }}>
                        {t("maintenance.createdAt")}: {item.createdAt ? Base.dateToDateTimeStr(item.createdAt) : t("common.none")}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                        {t("maintenance.createdBy")}: {item.createUserName ?? t("common.none")}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography sx={{ mb: 1 }}>
                        {t("maintenance.maintenanceDate")}: {item.maintenanceDate ? Base.dateToDateStr(item.maintenanceDate) : t("common.none")}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography sx={{ mb: 1 }}>
                        {t("maintenance.price")}: {item.price ?? t("common.none")}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {item.comment ? (
                        <>
                            <Typography sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
                                {t("maintenance.comment")}: {item.comment}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                        </>
                    ) : null}

                    {item.driverComment ? (
                        <>
                            <Typography sx={{ whiteSpace: "pre-wrap" }}>
                                {t("maintenance.driverComment")}: {item.driverComment}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                        </>
                    ) : null}

                    <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                        <Button variant="contained" onClick={() => navigate("/services")}>
                            {t("header.back")}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!canEdit}
                            onClick={() => navigate(`/services/${id}/edit`)}
                        >
                            {t("maintenance.edit")}
                        </Button>
                        {canShowMarkDone ? (
                            <Button
                                variant="contained"
                                color="success"
                                disabled={!canMarkDone || markDoneDisabledReason === "awaiting"}
                                onClick={() => {
                                    void dispatch(markVehicleMaintenanceDone(id))
                                        .unwrap()
                                        .then(() => dispatch(addSuccessMsg(t("maintenance.markDoneSuccess"))))
                                        .catch(handleApiError);
                                }}
                            >
                                {markDoneDisabledReason === "awaiting"
                                    ? t("maintenance.awaitingApproval")
                                    : t("maintenance.markDone")}
                            </Button>
                        ) : null}
                    </Box>
                </Box>
            )}
        </MainLayout>
    );
};
